import React from "react";
import {
  Typography,
  Box,
  Divider,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Healing as HealingIcon,
  Assignment as AssignmentIcon,
  LocalHospital as LocalHospitalIcon,
  TrackChanges as TrackChangesIcon,
  Science as ScienceIcon,
  Grading as GradingIcon,
  Analytics as AnalyticsIcon
} from "@mui/icons-material";
import reportData from "./imsBreastCancerReport.json";

const BreastCancerIMSCard: React.FC = () => {
  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: "100%", margin: "0 auto" }}>
      {/* Header Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }} variant="outlined">
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
          <Typography variant="h4" color="primary" fontWeight="bold" gutterBottom>
            {reportData.ims_breast_cancer_report.metadata.system_name}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {reportData.ims_breast_cancer_report.metadata.facility_id}
          </Typography>
          <Chip
            label={`Report ID: ${reportData.ims_breast_cancer_report.metadata.report_id}`}
            variant="outlined"
            sx={{ mt: 1 }}
          />
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
          <Typography variant="body2" color="text.secondary">
            Report Date: {new Date(reportData.ims_breast_cancer_report.metadata.report_date).toLocaleDateString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Version: {reportData.ims_breast_cancer_report.metadata.report_version}
          </Typography>
        </Box>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Data Sources: {reportData.ims_breast_cancer_report.metadata.data_sources.join(", ")}
          </Typography>
        </Box>
      </Paper>

      {/* Patient Demographics */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <PersonIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Patient Demographics</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Patient ID</Typography>
              <Typography variant="body1" gutterBottom>{reportData.ims_breast_cancer_report.patient.demographics.patient_id}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">MRN</Typography>
              <Typography variant="body1" gutterBottom>{reportData.ims_breast_cancer_report.patient.demographics.mrn}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Name</Typography>
              <Typography variant="body1" gutterBottom>
                {reportData.ims_breast_cancer_report.patient.demographics.name.first} {reportData.ims_breast_cancer_report.patient.demographics.name.last}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Date of Birth</Typography>
              <Typography variant="body1" gutterBottom>
                {new Date(reportData.ims_breast_cancer_report.patient.demographics.dob).toLocaleDateString()}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Gender</Typography>
              <Typography variant="body1" gutterBottom>{reportData.ims_breast_cancer_report.patient.demographics.gender}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Contact</Typography>
              <Typography variant="body1" gutterBottom>{reportData.ims_breast_cancer_report.patient.demographics.contact.email}</Typography>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">Clinical Team</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">Medical Oncologist</Typography>
              <Typography variant="body2" gutterBottom>{reportData.ims_breast_cancer_report.patient.clinical_team.medical_oncologist}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">Surgical Oncologist</Typography>
              <Typography variant="body2" gutterBottom>{reportData.ims_breast_cancer_report.patient.clinical_team.surgical_oncologist}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">Radiation Oncologist</Typography>
              <Typography variant="body2" gutterBottom>{reportData.ims_breast_cancer_report.patient.clinical_team.radiation_oncologist}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">Primary Care</Typography>
              <Typography variant="body2" gutterBottom>{reportData.ims_breast_cancer_report.patient.clinical_team.primary_care}</Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Diagnostic Data */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <HealingIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Diagnostic Data</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Presentation</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="body2"><strong>First Symptom Date:</strong> {new Date(reportData.ims_breast_cancer_report.diagnostic_data.presentation.date_first_symptom).toLocaleDateString()}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="body2"><strong>First Consult Date:</strong> {new Date(reportData.ims_breast_cancer_report.diagnostic_data.presentation.date_first_consult).toLocaleDateString()}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="body2">
                      <strong>Palpable Lump:</strong>
                      <Chip
                        label={reportData.ims_breast_cancer_report.diagnostic_data.presentation.initial_symptoms.lump.present ? 'Yes' : 'No'}
                        size="small"
                        color={reportData.ims_breast_cancer_report.diagnostic_data.presentation.initial_symptoms.lump.present ? 'primary' : 'default'}
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                  </Grid>
                  {reportData.ims_breast_cancer_report.diagnostic_data.presentation.initial_symptoms.lump.present && (
                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                      <Typography variant="body2"><strong>Location:</strong> {reportData.ims_breast_cancer_report.diagnostic_data.presentation.initial_symptoms.lump.location}</Typography>
                    </Grid>
                  )}
                  {reportData.ims_breast_cancer_report.diagnostic_data.presentation.initial_symptoms.lump.present && (
                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                      <Typography variant="body2"><strong>Size:</strong> {reportData.ims_breast_cancer_report.diagnostic_data.presentation.initial_symptoms.lump.size}</Typography>
                    </Grid>
                  )}
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="body2">
                      <strong>Nipple Retraction:</strong>
                      <Chip
                        label={reportData.ims_breast_cancer_report.diagnostic_data.presentation.initial_symptoms.nipple_retraction ? 'Yes' : 'No'}
                        size="small"
                        color={reportData.ims_breast_cancer_report.diagnostic_data.presentation.initial_symptoms.nipple_retraction ? 'primary' : 'default'}
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Imaging</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AssignmentIcon color="action" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2">Mammogram</Typography>
                    </Box>
                    <Typography variant="body2"><strong>BIRADS:</strong> {reportData.ims_breast_cancer_report.diagnostic_data.imaging.mammogram.birads}</Typography>
                    <Typography variant="body2"><strong>Findings:</strong> {reportData.ims_breast_cancer_report.diagnostic_data.imaging.mammogram.findings}</Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AssignmentIcon color="action" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2">Ultrasound</Typography>
                    </Box>
                    <Typography variant="body2"><strong>Size:</strong> {reportData.ims_breast_cancer_report.diagnostic_data.imaging.ultrasound.size}</Typography>
                    <Typography variant="body2"><strong>Features:</strong> {reportData.ims_breast_cancer_report.diagnostic_data.imaging.ultrasound.features}</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Biopsy</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="body2"><strong>Type:</strong> {reportData.ims_breast_cancer_report.diagnostic_data.biopsy.type}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="body2"><strong>Date:</strong> {new Date(reportData.ims_breast_cancer_report.diagnostic_data.biopsy.procedure_date).toLocaleDateString()}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="body2"><strong>Pathology Report ID:</strong> {reportData.ims_breast_cancer_report.diagnostic_data.biopsy.pathology_report_id}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Pathology */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ScienceIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Pathology</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Histology</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="body2"><strong>Type:</strong> {reportData.ims_breast_cancer_report.pathology.histology.type}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="body2"><strong>Nottingham Grade:</strong> {reportData.ims_breast_cancer_report.pathology.histology.nottingham_grade}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="body2"><strong>Tumor Size (Microscopic):</strong> {reportData.ims_breast_cancer_report.pathology.histology.tumor_size.microscopic}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2"><strong>Lymphovascular Invasion:</strong> {reportData.ims_breast_cancer_report.pathology.histology.lymphovascular_invasion}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2">
                      <strong>DCIS Component:</strong>
                      <Chip
                        label={reportData.ims_breast_cancer_report.pathology.histology.dcis_component.present ? 'Present' : 'Absent'}
                        size="small"
                        color={reportData.ims_breast_cancer_report.pathology.histology.dcis_component.present ? 'primary' : 'default'}
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Receptor Status</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="body2">
                      <strong>ER Status:</strong>
                      <Chip
                        label={reportData.ims_breast_cancer_report.pathology.receptors.er.status}
                        size="small"
                        color={reportData.ims_breast_cancer_report.pathology.receptors.er.status === 'Positive' ? 'primary' : 'default'}
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                    <Typography variant="body2"><strong>Percentage:</strong> {reportData.ims_breast_cancer_report.pathology.receptors.er.percentage}</Typography>
                    <Typography variant="body2"><strong>Intensity:</strong> {reportData.ims_breast_cancer_report.pathology.receptors.er.intensity}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="body2">
                      <strong>PR Status:</strong>
                      <Chip
                        label={reportData.ims_breast_cancer_report.pathology.receptors.pr.status}
                        size="small"
                        color={reportData.ims_breast_cancer_report.pathology.receptors.pr.status === 'Positive' ? 'primary' : 'default'}
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                    <Typography variant="body2"><strong>Percentage:</strong> {reportData.ims_breast_cancer_report.pathology.receptors.pr.percentage}</Typography>
                    <Typography variant="body2"><strong>Intensity:</strong> {reportData.ims_breast_cancer_report.pathology.receptors.pr.intensity}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="body2">
                      <strong>HER2 Status:</strong>
                      <Chip
                        label={reportData.ims_breast_cancer_report.pathology.receptors.her2.final_status}
                        size="small"
                        color={reportData.ims_breast_cancer_report.pathology.receptors.her2.final_status === 'Positive' ? 'primary' : 'default'}
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                    <Typography variant="body2"><strong>IHC:</strong> {reportData.ims_breast_cancer_report.pathology.receptors.her2.ihc}</Typography>
                    <Typography variant="body2"><strong>FISH Result:</strong> {reportData.ims_breast_cancer_report.pathology.receptors.her2.fish.result}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2"><strong>Ki67:</strong> {reportData.ims_breast_cancer_report.pathology.receptors.ki67}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Molecular Profile</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2"><strong>Oncotype DX Recurrence Score:</strong> {reportData.ims_breast_cancer_report.pathology.molecular_profile.oncotype_dx.recurrence_score}</Typography>
                    <Typography variant="body2"><strong>Risk Category:</strong> {reportData.ims_breast_cancer_report.pathology.molecular_profile.oncotype_dx.risk_category}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2">
                      <strong>PD-L1 Status:</strong>
                      <Chip
                        label={reportData.ims_breast_cancer_report.pathology.molecular_profile.pdl1.status}
                        size="small"
                        color={reportData.ims_breast_cancer_report.pathology.molecular_profile.pdl1.status === 'Positive' ? 'primary' : 'default'}
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                    <Typography variant="body2"><strong>CPS:</strong> {reportData.ims_breast_cancer_report.pathology.molecular_profile.pdl1.cps}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Staging */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <GradingIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Staging</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Clinical Stage</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                <Typography variant="h6" color="secondary" gutterBottom>
                  {reportData.ims_breast_cancer_report.staging.clinical}
                </Typography>
                <Typography variant="body2"><strong>AJCC 8th Edition:</strong> Stage {reportData.ims_breast_cancer_report.staging.ajcc_8th_edition.stage_group}</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Pathological Stage</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                <Typography variant="h6" color="secondary" gutterBottom>
                  {reportData.ims_breast_cancer_report.staging.pathological}
                </Typography>
                <Grid container spacing={1}>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="body2"><strong>T:</strong> {reportData.ims_breast_cancer_report.staging.ajcc_8th_edition.t}</Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="body2"><strong>N:</strong> {reportData.ims_breast_cancer_report.staging.ajcc_8th_edition.n}</Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="body2"><strong>M:</strong> {reportData.ims_breast_cancer_report.staging.ajcc_8th_edition.m}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Treatment */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <LocalHospitalIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Treatment</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Surgical Treatment</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                <Typography variant="body2"><strong>Procedure:</strong> {reportData.ims_breast_cancer_report.treatment.surgical.procedure}</Typography>
                <Typography variant="body2"><strong>Date:</strong> {new Date(reportData.ims_breast_cancer_report.treatment.surgical.date).toLocaleDateString()}</Typography>
                <Typography variant="body2"><strong>Margins:</strong> {reportData.ims_breast_cancer_report.treatment.surgical.margins.status}</Typography>
                <Typography variant="body2"><strong>Closest Margin:</strong> {reportData.ims_breast_cancer_report.treatment.surgical.margins.closest_margin}</Typography>
                <Typography variant="body2"><strong>Lymph Nodes Removed:</strong> {reportData.ims_breast_cancer_report.treatment.surgical.lymph_nodes.sentinel_nodes_removed}</Typography>
                <Typography variant="body2"><strong>Positive Nodes:</strong> {reportData.ims_breast_cancer_report.treatment.surgical.lymph_nodes.positive_nodes}</Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Systemic Treatment</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                <Typography variant="body2"><strong>Chemotherapy Regimen:</strong> {reportData.ims_breast_cancer_report.treatment.systemic.chemotherapy.regimen}</Typography>
                <Typography variant="body2"><strong>Start Date:</strong> {new Date(reportData.ims_breast_cancer_report.treatment.systemic.chemotherapy.start_date).toLocaleDateString()}</Typography>
                <Typography variant="body2"><strong>Cycles Planned:</strong> {reportData.ims_breast_cancer_report.treatment.systemic.chemotherapy.cycles_planned}</Typography>
                <Typography variant="body2"><strong>Completed Cycles:</strong> {reportData.ims_breast_cancer_report.treatment.systemic.chemotherapy.completed_cycles}</Typography>
                <Typography variant="body2"><strong>Endocrine Therapy:</strong> {reportData.ims_breast_cancer_report.treatment.systemic.endocrine.planned_therapy}</Typography>
                <Typography variant="body2"><strong>Duration:</strong> {reportData.ims_breast_cancer_report.treatment.systemic.endocrine.duration}</Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Radiation Therapy</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                <Typography variant="body2">
                  <strong>Planned:</strong>
                  <Chip
                    label={reportData.ims_breast_cancer_report.treatment.radiation.planned ? 'Yes' : 'No'}
                    size="small"
                    color={reportData.ims_breast_cancer_report.treatment.radiation.planned ? 'primary' : 'default'}
                    sx={{ ml: 1 }}
                  />
                </Typography>
                {reportData.ims_breast_cancer_report.treatment.radiation.planned && (
                  <>
                    <Typography variant="body2"><strong>Start Date:</strong> {new Date(reportData.ims_breast_cancer_report.treatment.radiation.start_date).toLocaleDateString()}</Typography>
                    <Typography variant="body2"><strong>Technique:</strong> {reportData.ims_breast_cancer_report.treatment.radiation.technique}</Typography>
                    <Typography variant="body2"><strong>Dose:</strong> {reportData.ims_breast_cancer_report.treatment.radiation.dose}</Typography>
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Follow-up */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TrackChangesIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Follow-up & Outcomes</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Surveillance Plan</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                {reportData.ims_breast_cancer_report.follow_up.surveillance_plan.schedule.map((item, index) => (
                  <Box key={index} sx={{ mb: index > 0 ? 2 : 0 }}>
                    <Typography variant="body2" fontWeight="bold">{item.interval} ({item.duration})</Typography>
                    <Typography variant="body2">{item.components.join(", ")}</Typography>
                    {index < reportData.ims_breast_cancer_report.follow_up.surveillance_plan.schedule.length - 1 && <Divider sx={{ my: 1 }} />}
                  </Box>
                ))}
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Patient Reported Outcomes</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                <Typography variant="body2"><strong>Last Assessment:</strong> {new Date(reportData.ims_breast_cancer_report.follow_up.patient_reported_outcomes.last_assessment).toLocaleDateString()}</Typography>
                <Typography variant="body2"><strong>Pain Score:</strong> {reportData.ims_breast_cancer_report.follow_up.patient_reported_outcomes.pain_score}/10</Typography>
                <Typography variant="body2"><strong>Fatigue Score:</strong> {reportData.ims_breast_cancer_report.follow_up.patient_reported_outcomes.fatigue_score}/10</Typography>
                <Typography variant="body2"><strong>Emotional Wellbeing:</strong> {reportData.ims_breast_cancer_report.follow_up.patient_reported_outcomes.emotional_wellbeing}/5</Typography>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Quality Metrics */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <AnalyticsIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Quality Metrics</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%', textAlign: 'center' }}>
                <Typography variant="h4" color="primary" gutterBottom>
                  {reportData.ims_breast_cancer_report.quality_metrics.nccn_guideline_compliance}%
                </Typography>
                <Typography variant="body2">NCCN Guideline Compliance</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%', textAlign: 'center' }}>
                <Typography variant="h4" color="primary" gutterBottom>
                  {reportData.ims_breast_cancer_report.quality_metrics.time_to_treatment_initiation}
                </Typography>
                <Typography variant="body2">Days to Treatment Initiation</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%', textAlign: 'center' }}>
                <Typography variant="h6" color="primary" gutterBottom>
                  {reportData.ims_breast_cancer_report.quality_metrics.surgical_quality_indicators.margin_status}
                </Typography>
                <Typography variant="body2">Margin Status</Typography>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default BreastCancerIMSCard;